import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Trash2, Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import PasswordInput from "@/components/PasswordInput";

/** "Delete my account" — the entry point plus its confirmation dialog.
 * Required by Google Play and the App Store. What the server erases/keeps
 * (notably: tracks fans already bought are never taken from their
 * libraries) is documented in backend/account_deletion.py. */
export default function DeleteAccountPanel({ className = "" }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Set once the server answered FUNDS_REMAINING: the next submit then sends
  // the explicit "I accept losing the rest of my balance" confirmation.
  const [forfeit, setForfeit] = useState(false);

  // The single admin account can't be deleted (server refuses it too).
  if (!user || user.role === "admin") return null;
  const d = t.deleteAccount;

  const close = () => {
    setOpen(false);
    setPassword("");
    setError("");
    setForfeit(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      // Erasing an artist's files can take well over the default timeout.
      await api.post("/auth/delete-account", { password, confirm_forfeit_funds: forfeit }, { timeout: 120000 });
      toast.success(d.done);
      logout();
      navigate("/auth", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "FUNDS_REMAINING") setForfeit(true);
      setError(err.response ? formatApiError(detail) : d.network);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 text-xs font-semibold text-red-400/80 hover:text-red-300 transition ${className}`}
        data-testid="delete-account-open"
      >
        <Trash2 className="w-3.5 h-3.5" /> {d.link}
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md grid place-items-center p-4" data-testid="delete-account-modal">
            <form
              onSubmit={submit}
              className="relative w-full max-w-md max-h-[90dvh] overflow-y-auto bg-zinc-950 border border-red-500/20 rounded-3xl p-6 space-y-4"
            >
              <button type="button" onClick={close} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400" aria-label="close">
                <X className="w-4 h-4" />
              </button>
              <h3 className="text-xl font-black font-display text-white pr-10">{d.title}</h3>

              <div className="flex gap-3 p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-sm text-red-100/90 leading-relaxed">
                <AlertTriangle className="w-5 h-5 shrink-0 text-red-400 mt-0.5" />
                <p>{user.role === "artist" ? d.warnArtist : d.warnFan}</p>
              </div>
              {user.affiliate_program && (
                <p className="text-sm rounded-2xl px-3 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-100" data-testid="delete-account-affiliate-warning">
                  Programme d'affiliation : toutes tes commissions non retirées seront perdues et reversées à Metazik. Les personnes inscrites avec ton code promo gardent leurs avantages.
                </p>
              )}

              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">{d.passwordLabel}</span>
                <div className="mt-1">
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.auth.password}
                    dataTestid="delete-account-password"
                    autoComplete="current-password"
                  />
                </div>
              </label>

              {error && (
                <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2" data-testid="delete-account-error">
                  {error}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={close} className="btn-secondary flex-1" data-testid="delete-account-cancel">
                  {d.cancel}
                </button>
                <button
                  type="submit"
                  disabled={busy || !password}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-bold disabled:opacity-50 transition"
                  data-testid="delete-account-confirm"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {forfeit ? d.confirmForfeit : d.confirm}
                </button>
              </div>
            </form>
          </div>,
          document.body
        )}
    </>
  );
}
